// =============================================================================
// PEDRA — src/components/EdgeBandingModal.tsx
// Modal de fita de borda. Com uma peca selecionada, mostra o desenho da peca
// com os 4 lados clicaveis e um seletor de fita por lado (superior, esquerda,
// inferior, direita), cada um exibindo a dimensao daquele lado.
// Com `part === null`, abre apenas o cadastro de tipos de fita.
// =============================================================================

import { useEffect, useId, useRef, useState } from 'react';
import type { ChangeEvent, ReactElement } from 'react';

import type {
  EdgeBanding,
  EdgeBandType,
  EdgeSide,
  Id,
  Micron,
  Part,
  Settings,
  Unit,
} from '../core/types';
import { formatDimensions, formatLength, fromMicron, parseDimension } from '../core/units';
import { isValidHexColor, LIMITS } from '../core/validation';
import { Modal } from './Modal';
import { HelpHint } from './OptionsPanel';

export interface EdgeBandingModalProps {
  open: boolean;
  /** Peca sendo editada; `null` abre so o cadastro de tipos de fita. */
  part: Part | null;
  edgeBands: readonly EdgeBandType[];
  settings: Settings;
  onChangePart(id: Id, patch: Partial<Part>): void;
  onAddBand(): void;
  onUpdateBand(id: Id, patch: Partial<EdgeBandType>): void;
  onRemoveBand(id: Id): void;
  onClose(): void;
}

/** Valor especial do `<select>` que dispara a criacao de um tipo novo. */
const CREATE_VALUE = '__novo__';

/** Ordem dos seletores na tela — a mesma do CutList Optimizer. */
const SIDE_ORDER: readonly EdgeSide[] = ['top', 'left', 'bottom', 'right'];

const SIDE_LABEL: Readonly<Record<EdgeSide, string>> = {
  top: 'Fita superior',
  left: 'Fita esquerda',
  bottom: 'Fita inferior',
  right: 'Fita direita',
};

const SIDE_SHORT: Readonly<Record<EdgeSide, string>> = {
  top: 'Superior',
  left: 'Esquerda',
  bottom: 'Inferior',
  right: 'Direita',
};

const HELP_SIDE =
  'A fita é colada nesta borda. A espessura escolhida é descontada da medida ' +
  'de corte para que a peça pronta fique com a medida informada na tabela.';

/** Copia imutavel da fita trocando um lado. Evita chave computada no objeto. */
function withSide(banding: EdgeBanding, side: EdgeSide, value: Id | null): EdgeBanding {
  return {
    top: side === 'top' ? value : banding.top,
    right: side === 'right' ? value : banding.right,
    bottom: side === 'bottom' ? value : banding.bottom,
    left: side === 'left' ? value : banding.left,
  };
}

/** Lado superior/inferior medem a LARGURA; esquerda/direita, o COMPRIMENTO. */
function sideLength(part: Part, side: EdgeSide): Micron {
  return side === 'top' || side === 'bottom' ? part.width : part.length;
}

// -----------------------------------------------------------------------------
// ENTRADA COMPACTA DE ESPESSURA
// -----------------------------------------------------------------------------

interface ThicknessInputProps {
  id: string;
  value: Micron;
  unit: Unit;
  decimals: number;
  onCommit(value: Micron): void;
}

/**
 * Campo pequeno de espessura usado na tabela de tipos de fita. Mesma logica do
 * `DimensionField`: comita a cada digitacao valida e so reformata quando o
 * valor muda por fora.
 */
function ThicknessInput(props: ThicknessInputProps): ReactElement {
  const { id, value, unit, decimals, onCommit } = props;

  const [draft, setDraft] = useState<string>(() => fromMicron(value, unit, decimals));
  const [invalid, setInvalid] = useState(false);
  const lastValueRef = useRef<Micron>(value);
  const lastUnitRef = useRef<Unit>(unit);

  useEffect(() => {
    if (value === lastValueRef.current && unit === lastUnitRef.current) return;
    lastValueRef.current = value;
    lastUnitRef.current = unit;
    setDraft(fromMicron(value, unit, decimals));
    setInvalid(false);
  }, [value, unit, decimals]);

  const handleChange = (raw: string): void => {
    setDraft(raw);
    const parsed = parseDimension(raw.trim(), unit);
    if (parsed === null || parsed <= 0 || parsed > LIMITS.MAX_KERF) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    lastValueRef.current = parsed;
    onCommit(parsed);
  };

  return (
    <input
      id={id}
      className="opt-input opt-input--compact tabular"
      type="text"
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      value={draft}
      aria-invalid={invalid}
      aria-label="Espessura da fita"
      title="Espessura da fita, descontada da medida de corte da peça"
      onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
      onBlur={() => {
        setDraft(fromMicron(value, unit, decimals));
        setInvalid(false);
      }}
    />
  );
}

// -----------------------------------------------------------------------------
// MODAL
// -----------------------------------------------------------------------------

export function EdgeBandingModal(props: EdgeBandingModalProps): ReactElement {
  const {
    open,
    part,
    edgeBands,
    settings,
    onChangePart,
    onAddBand,
    onUpdateBand,
    onRemoveBand,
    onClose,
  } = props;

  const uid = useId();
  const { unit, decimalPlaces, dimensionOrder } = settings;

  /** Lado que pediu "Criar novo tipo" e aguarda o id do tipo recem-criado. */
  const pendingSideRef = useRef<EdgeSide | null>(null);
  const bandCountRef = useRef(edgeBands.length);
  const [newBandId, setNewBandId] = useState<Id | null>(null);
  const selectRefs = useRef<Partial<Record<EdgeSide, HTMLSelectElement | null>>>({});

  // Detecta o tipo de fita recem-criado: `onAddBand()` nao devolve o id, entao
  // observamos o crescimento da lista e usamos o ultimo elemento.
  useEffect(() => {
    const previous = bandCountRef.current;
    bandCountRef.current = edgeBands.length;
    if (edgeBands.length <= previous) return;

    const created = edgeBands[edgeBands.length - 1];
    const side = pendingSideRef.current;
    pendingSideRef.current = null;
    if (!created) return;

    setNewBandId(created.id);
    if (side && part) {
      onChangePart(part.id, { edgeBanding: withSide(part.edgeBanding, side, created.id) });
    }
  }, [edgeBands, part, onChangePart]);

  // Ao fechar, limpa o destaque da linha nova.
  useEffect(() => {
    if (!open) {
      setNewBandId(null);
      pendingSideRef.current = null;
    }
  }, [open]);

  const bandById = (id: Id | null): EdgeBandType | null => {
    if (!id) return null;
    return edgeBands.find((b) => b.id === id) ?? null;
  };

  const handleSideChange = (side: EdgeSide, raw: string): void => {
    if (!part) return;
    if (raw === CREATE_VALUE) {
      pendingSideRef.current = side;
      onAddBand();
      return;
    }
    const value: Id | null = raw === '' ? null : raw;
    onChangePart(part.id, { edgeBanding: withSide(part.edgeBanding, side, value) });
  };

  const focusSide = (side: EdgeSide): void => {
    const el = selectRefs.current[side];
    if (el) el.focus();
  };

  const clearAllSides = (): void => {
    if (!part) return;
    onChangePart(part.id, { edgeBanding: { top: null, right: null, bottom: null, left: null } });
  };

  const applyToAllSides = (): void => {
    if (!part) return;
    const first = edgeBands[0];
    if (!first) return;
    // Usa a fita ja escolhida em algum lado; se nao houver, a primeira da lista.
    const current =
      part.edgeBanding.top ??
      part.edgeBanding.right ??
      part.edgeBanding.bottom ??
      part.edgeBanding.left ??
      first.id;
    onChangePart(part.id, {
      edgeBanding: { top: current, right: current, bottom: current, left: current },
    });
  };

  const sideColor = (side: EdgeSide): string => {
    const band = part ? bandById(part.edgeBanding[side]) : null;
    if (!band) return 'transparent';
    return isValidHexColor(band.color) ? band.color : '#8C8C8C';
  };

  const bandedSides = part
    ? SIDE_ORDER.filter((s) => part.edgeBanding[s] !== null).length
    : 0;

  const title = part ? 'Fita de borda da peça' : 'Tipos de fita de borda';

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      size="md"
      footer={
        <>
          {part ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={clearAllSides}
              disabled={bandedSides === 0}
              title="Remove a fita dos quatro lados desta peça"
            >
              Tirar todas as fitas
            </button>
          ) : null}
          <span className="modal-footer__spacer" />
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Concluído
          </button>
        </>
      }
    >
      {part ? (
        <section className="modal-section">
          <p className="modal-eb-part">
            <strong>{part.label.trim() !== '' ? part.label : 'Peça sem nome'}</strong>{' '}
            <span className="tabular">
              {formatDimensions(part.length, part.width, unit, decimalPlaces, dimensionOrder)}
            </span>
          </p>

          {/* Desenho da peca: cada faixa e um botao que leva ao seletor. */}
          <div className="modal-eb-figure" role="group" aria-label="Lados da peça">
            {SIDE_ORDER.map((side) => (
              <button
                key={side}
                type="button"
                className={`modal-eb-side modal-eb-side--${side}`}
                style={{ backgroundColor: sideColor(side) }}
                data-on={part.edgeBanding[side] !== null}
                onClick={() => focusSide(side)}
                title={`${SIDE_SHORT[side]} — ${
                  bandById(part.edgeBanding[side])?.name.trim() || 'sem fita'
                }`}
                aria-label={`Escolher a fita do lado ${SIDE_SHORT[side].toLowerCase()}`}
              />
            ))}
            <span className="modal-eb-center tabular" aria-hidden="true">
              {formatDimensions(part.length, part.width, unit, decimalPlaces, dimensionOrder)}
            </span>
          </div>

          <div className="modal-eb-sides">
            {SIDE_ORDER.map((side) => {
              const selectId = `${uid}-${side}`;
              const value = part.edgeBanding[side] ?? '';
              return (
                <div className="opt-field" key={side}>
                  <div className="opt-field__head">
                    <label className="opt-field__label" htmlFor={selectId}>
                      {SIDE_LABEL[side]}
                    </label>
                    <HelpHint text={HELP_SIDE} />
                  </div>
                  <select
                    id={selectId}
                    className="opt-select"
                    value={value}
                    title={HELP_SIDE}
                    ref={(el) => {
                      selectRefs.current[side] = el;
                    }}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      handleSideChange(side, e.target.value)
                    }
                  >
                    <option value="">Nenhuma</option>
                    {edgeBands.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name.trim() !== '' ? b.name : 'Fita sem nome'} —{' '}
                        {formatLength(b.thickness, unit, decimalPlaces)}
                      </option>
                    ))}
                    <option value={CREATE_VALUE}>+ Criar novo tipo…</option>
                  </select>
                  <p className="opt-field__help tabular">
                    {SIDE_SHORT[side]}: {formatLength(sideLength(part, side), unit, decimalPlaces)}
                  </p>
                </div>
              );
            })}
          </div>

          {edgeBands.length > 0 ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={applyToAllSides}
              title="Repete a mesma fita nos quatro lados"
            >
              Aplicar nos quatro lados
            </button>
          ) : null}
        </section>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      <section className="modal-section">
        <h3 className="modal-section__title">Tipos de fita cadastrados</h3>

        {edgeBands.length === 0 ? (
          <p className="modal-empty">
            Nenhum tipo de fita cadastrado ainda. Crie um tipo informando nome,
            espessura e cor — a espessura é descontada da medida de corte das peças.
          </p>
        ) : (
          <table className="modal-eb-table">
            <thead>
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">Espessura</th>
                <th scope="col">Cor</th>
                <th scope="col">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {edgeBands.map((band) => (
                <tr key={band.id} data-new={band.id === newBandId}>
                  <td>
                    <input
                      className="opt-input"
                      type="text"
                      value={band.name}
                      maxLength={LIMITS.MAX_NAME_LENGTH}
                      placeholder="Ex.: PVC 22 × 0,45 branco"
                      aria-label="Nome do tipo de fita"
                      autoFocus={band.id === newBandId}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onUpdateBand(band.id, { name: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <ThicknessInput
                      id={`${uid}-esp-${band.id}`}
                      value={band.thickness}
                      unit={unit}
                      decimals={decimalPlaces}
                      onCommit={(thickness) => onUpdateBand(band.id, { thickness })}
                    />
                  </td>
                  <td>
                    <input
                      className="modal-eb-color"
                      type="color"
                      value={isValidHexColor(band.color) ? band.color : '#8C8C8C'}
                      aria-label="Cor da fita no desenho"
                      title="Cor usada para desenhar a fita na borda da peça"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onUpdateBand(band.id, { color: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      onClick={() => onRemoveBand(band.id)}
                      title="Excluir este tipo de fita e tirá-lo das peças"
                      aria-label={`Excluir a fita ${band.name.trim() || 'sem nome'}`}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            pendingSideRef.current = null;
            onAddBand();
          }}
          title="Cadastra mais um tipo de fita"
        >
          + Adicionar tipo de fita
        </button>
      </section>
    </Modal>
  );
}

export default EdgeBandingModal;
